import { Component, type ErrorInfo, type ReactNode } from "react";
import { captureException } from "../lib/shared/sentry";

interface Props {
  children: ReactNode;
}

interface State {
  chunkError: Error | null;
}

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Loading chunk [\w.-]+ failed/i,
  /Loading CSS chunk [\w.-]+ failed/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
];

export function isChunkLoadError(error: Error): boolean {
  const message = error.message || "";
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

/**
 * Error boundary that catches lazy-loaded chunk failures (stale deployments,
 * network hiccups) and offers a page reload instead of a dead screen.
 *
 * Non-chunk errors are re-thrown so the parent ErrorBoundary handles them.
 */
export class ChunkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { chunkError: null };
  }

  static getDerivedStateFromError(error: Error): State | null {
    if (isChunkLoadError(error)) {
      return { chunkError: error };
    }
    // Re-throw non-chunk errors to parent boundary
    throw error;
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack ?? "" } },
      tags: { type: "chunk_load_error" },
    });
  }

  render() {
    if (this.state.chunkError) {
      return (
        <div className="min-h-[200px] flex items-center justify-center p-8" role="alert">
          <div className="max-w-md text-center">
            <h2 className="text-lg font-semibold text-surface-900 dark:text-white mb-2">
              Page failed to load
            </h2>
            <p className="text-sm text-surface-600 dark:text-surface-400 mb-4">
              A new version may have been deployed, or the network request failed. Reloading the
              page should fix this.
            </p>
            <button
              onClick={() => {
                // Append cache-busting param so CDN serves fresh HTML
                const url = new URL(window.location.href);
                url.searchParams.set("_cb", Date.now().toString());
                window.location.replace(url.toString());
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
